import {openDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {ReactElement} from 'react';

import {defaultTo, find, propEq} from 'ramda';

import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {GetCoreExchangeResponse, useGetWarehousesQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {getWarehouseById} from '@dms/warehouse/shared';

import {Nullish} from 'shared/web';

import {CancelDeliveryDialog} from '../components/dialogs/CancelDeliveryDialog';
import {CustomerDeliveryDialog} from '../components/dialogs/CustomerDeliveryDialog';
import {GeneralSettingsDialog} from '../components/dialogs/GeneralSettingsDialog';
import {InspectionDialog} from '../components/dialogs/InspectionDialog';
import {ReturnSettingsDialog} from '../components/dialogs/ReturnSettingsDialog';
import {RmaAuthorizationDialog} from '../components/dialogs/RmaAuthorizationDialog';
import {RmaDenialDialog} from '../components/dialogs/RmaDenialDialog';
import {RmaRequestDialog} from '../components/dialogs/RmaRequestDialog';
import {WarehouseReceiptDialog} from '../components/dialogs/WarehouseReceiptDialog';

const dialogTestId = (dialog: string) =>
  testIds.warehouse.coreExchangeDetailOverview(`dialog.${dialog}`);

/**
 * Opens the dialog behind each core exchange action and the two edit dialogs of the detail.
 * `onSaved` refetches the detail after a successful PATCH; the action bar refetches through the
 * `headerDynamicActions` tag the mutations invalidate.
 */
export function useCoreExchangeDialogs(
  coreExchangeId: string,
  coreExchange: GetCoreExchangeResponse | Nullish,
  onSaved: VoidFunction
) {
  const {data: currentUser} = useGetCurrentUserInfoQuery();
  const {data: warehouses} = useGetWarehousesQuery();

  const currentUserId = defaultTo('', currentUser?.id);
  const coreItem = find(propEq('CORE', 'itemType'), defaultTo([], coreExchange?.tabs.items.items));

  const openSmallDialog = (content: ReactElement, id: string, title: string) =>
    openDialog(content, {id, title, size: 'small', 'data-testid': dialogTestId(id)});

  const openGeneralSettings = () =>
    openSmallDialog(
      <GeneralSettingsDialog
        coreExchangeId={coreExchangeId}
        general={coreExchange?.tabs.items.general}
        onSaved={onSaved}
        data-testid={dialogTestId('generalSettings')}
      />,
      'generalSettings',
      i18n.t('entity.warehouse.labels.general')
    );

  const openReturnSettings = () =>
    openSmallDialog(
      <ReturnSettingsDialog
        coreExchangeId={coreExchangeId}
        settings={coreExchange?.tabs.returnToSupplier?.settings}
        onSaved={onSaved}
        data-testid={dialogTestId('returnSettings')}
      />,
      'returnSettings',
      i18n.t('entity.warehouse.labels.coreExchangeReturnSettings')
    );

  const handleActionClick = (actionKey: string, actionTitle: string) =>
    match(actionKey)
      .with('DELIVERED_BY_CUSTOMER', () =>
        openSmallDialog(
          <CustomerDeliveryDialog
            coreExchangeId={coreExchangeId}
            currentUserId={currentUserId}
            onSaved={onSaved}
            data-testid={dialogTestId('customerDelivery')}
          />,
          'customerDelivery',
          actionTitle
        )
      )
      .with('CORE_WILL_NOT_BE_DELIVERED', () =>
        openSmallDialog(
          <CancelDeliveryDialog
            coreExchangeId={coreExchangeId}
            onSaved={onSaved}
            data-testid={dialogTestId('cancelDelivery')}
          />,
          'cancelDelivery',
          actionTitle
        )
      )
      .with('RECORD_INSPECTION', () =>
        openSmallDialog(
          <InspectionDialog
            coreExchangeId={coreExchangeId}
            currentUserId={currentUserId}
            onSaved={onSaved}
            data-testid={dialogTestId('inspection')}
          />,
          'inspection',
          actionTitle
        )
      )
      .with('RECEIVE_TO_WAREHOUSE', () =>
        openSmallDialog(
          <WarehouseReceiptDialog
            coreExchangeId={coreExchangeId}
            warehouseName={getWarehouseById(warehouses, coreItem?.warehouseId)?.name}
            onSaved={onSaved}
            data-testid={dialogTestId('warehouseReceipt')}
          />,
          'warehouseReceipt',
          actionTitle
        )
      )
      .with('SEND_RETURN_AUTHORISATION_REQUEST', () =>
        openSmallDialog(
          <RmaRequestDialog
            coreExchangeId={coreExchangeId}
            onSaved={onSaved}
            data-testid={dialogTestId('rmaRequest')}
          />,
          'rmaRequest',
          actionTitle
        )
      )
      .with('RETURN_AUTHORISED', () =>
        openSmallDialog(
          <RmaAuthorizationDialog
            coreExchangeId={coreExchangeId}
            rmaRequestDate={
              coreExchange?.tabs.returnToSupplier?.authorizationForReturn?.rmaRequestDate
            }
            onSaved={onSaved}
            data-testid={dialogTestId('rmaAuthorization')}
          />,
          'rmaAuthorization',
          actionTitle
        )
      )
      .with('RETURN_DENIED', () =>
        openSmallDialog(
          <RmaDenialDialog
            coreExchangeId={coreExchangeId}
            onSaved={onSaved}
            data-testid={dialogTestId('rmaDenial')}
          />,
          'rmaDenial',
          actionTitle
        )
      )
      .otherwise(() => undefined);

  return {handleActionClick, openGeneralSettings, openReturnSettings};
}
